import { formatTimeValue, roundedNowTimeParts } from "@/components/foundations/inputs/TimeInputField/TimeInputField";
import TaskTimingSection from "@/components/tasks/timing/TaskTimingSection/TaskTimingSection";
import type { TaskTimingValue } from "@/components/tasks/timing/TaskTimingFields/TaskTimingFields";
import { formatDateOnly, formatTimeDisplay, parseDateKey, timeStringToMinutes, todayKey } from "@/lib/time";
import { formatMinutesForDisplay, formatTimeForDisplay, type TimeDisplayFormat } from "@/lib/displayFormat";
import { addDaysToDateKey } from "@/lib/dateSplit";
import { setTaskTimingStart, setTaskTimingStartDate, type TaskScheduleValue } from "@/lib/taskTiming";

type Colors = { bar: string; soft: string; ink: string };

export type TaskScheduleFormValue = TaskScheduleValue;

export type TaskScheduleChangeReason = "date" | "start-time" | "duration" | "alert" | "today" | "now" | "clear";

export type TaskScheduleFormProps = {
  value: TaskScheduleFormValue;
  onChange: (value: TaskScheduleFormValue, reason: TaskScheduleChangeReason) => void;
  onCommit?: (value: TaskScheduleFormValue, reason: TaskScheduleChangeReason) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alwaysOpen?: boolean;
  colors: Colors;
  summaryParts?: string[];
  emptyLabel?: string;
};

export function scheduleSummaryParts({
  date,
  startTime,
  durationMin,
  format = "compact",
}: {
  date: string;
  startTime: string;
  durationMin: string;
  format?: TimeDisplayFormat;
}) {
  const startMinutes = startTime ? timeStringToMinutes(startTime) : null;
  const durationValue = parseScheduleDuration(durationMin);
  return [
    date ? formatTaskDate(date) : "",
    startMinutes != null ? formatTimeForDisplay(startMinutes, format) : "",
    durationValue ? formatMinutesForDisplay(durationValue, format) : "",
  ].filter(Boolean);
}

export function parseScheduleDuration(value: string) {
  const raw = value.trim();
  if (!raw) return null;
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export default function TaskScheduleForm({
  value,
  onChange,
  onCommit,
  open,
  onOpenChange,
  alwaysOpen = false,
  colors,
  summaryParts,
  emptyLabel = "Schedule",
}: TaskScheduleFormProps) {
  function clearSchedule() {
    onChange({
      timing: { startDate: "", endDate: "", startTime: "", endTime: "", durationMinutes: "" },
      alertEnabled: false,
    }, "clear");
  }

  function setNow() {
    const { h, m } = roundedNowTimeParts();
    const date = todayKey();
    onChange({
      ...value,
      timing: setTaskTimingStart(value.timing, date, formatTimeValue(h, m)),
    }, "now");
  }

  return (
    <TaskTimingSection
      kind="schedule"
      className="task-schedule-form"
      colors={colors}
      open={open || alwaysOpen}
      expandable={!alwaysOpen}
      onOpenChange={next => { if (!alwaysOpen) onOpenChange(next); }}
      emptyLabel={summaryParts?.length ? summaryParts.join(" • ") : emptyLabel}
      timing={{
        value: value.timing,
        onChange: (next, reason) => {
          const changeReason: TaskScheduleChangeReason = reason === "start-date" || reason === "end-date"
            ? "date"
            : reason === "start-time"
              ? "start-time"
              : "duration";
          onChange({ ...value, timing: next }, changeReason);
        },
        onDurationBlur: () => onCommit?.(value, "duration"),
      }}
      alertEnabled={value.alertEnabled}
      onAlertEnabledChange={alertEnabled => onChange({ ...value, alertEnabled }, "alert")}
      onToday={() => {
        const date = todayKey();
        onChange({ ...value, timing: setTaskTimingStartDate(value.timing, date, false) }, "today");
      }}
      onNow={setNow}
      onClear={clearSchedule}
    />
  );
}

export function scheduleTimingValue(date: string, startTime: string, durationMin: string): TaskTimingValue {
  const durationValue = parseScheduleDuration(durationMin);
  const startMinutes = startTime ? timeStringToMinutes(startTime) : null;
  const calculatedEndMinutes = durationValue && startMinutes != null
    ? (startMinutes + durationValue) % (24 * 60)
    : null;
  return {
    startDate: date,
    endDate: date && durationValue && startMinutes != null
      ? addDaysToDateKey(date, Math.floor((startMinutes + durationValue) / (24 * 60)))
      : date,
    startTime,
    endTime: calculatedEndMinutes == null ? "" : formatTimeDisplay(calculatedEndMinutes),
    durationMinutes: durationMin,
  };
}

function formatTaskDate(value: string) {
  try {
    return formatDateOnly(parseDateKey(value));
  } catch {
    return value;
  }
}
